import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { Observable, Subject, forkJoin, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { Socket, io } from 'socket.io-client';
import { environment } from '../../environments/environment';
import { IndexService } from './index';

export interface TaskNotificationItem {
  id: string;
  jobId?: string;
  taskId?: string;
  indexName?: string;
  label: string;
  type: 'DOCUMENT' | 'WEB';
  status: 'waiting' | 'active' | 'cancelling' | 'cancelled' | 'completed' | 'error';
  step?: string;
  progress: number;
  progressMessage?: string;
  error?: string;
  updatedAt: Date;
}

@Injectable({
  providedIn: 'root',
})
export class TaskService {
  private readonly http = inject(HttpClient);
  private readonly indexService = inject(IndexService);

  private readonly socket: Socket;
  private readonly taskEvents = new Subject<any>();

  // Segnale centrale reattivo per tutti i task (coda + notifiche)
  readonly tasks = signal<TaskNotificationItem[]>([]);

  // Conteggio task attualmente in coda o in lavorazione
  readonly activeCount = computed(() =>
    this.tasks().filter((t) => TaskService.isPending(t.status)).length
  );

  // Indica se ci sono task in corso
  readonly hasActiveTasks = computed(() => this.activeCount() > 0);

  constructor() {
    this.socket = io(environment.host, {
      path: environment.socketPath,
      transports: ['websocket'],
    });

    this.socket.on('connect', () => {
      console.log('TaskService: WebSocket connected, reloading queued tasks...');
      this.loadAllQueuedTasks();
    });

    this.taskListeners();
    this.loadAllQueuedTasks();
  }

  public getQueuedTasks(indexName: string, type: 'DOCUMENT_INGESTION' | 'WEB_CRAWL') {
    const httpOptions = {
      params: new HttpParams().set('type', type),
    };
    return this.http.get<any[]>(`${environment.apiUrl}/task/queued/${indexName}`, httpOptions);
  }

  // Carica i task in coda per tutti gli indici per ricostruire lo stato dopo un refresh (F5)
  public loadAllQueuedTasks() {
    this.indexService
      .getIndices()
      .pipe(
        catchError((err) => {
          console.warn('TaskService: Impossibile recuperare indici per i task:', err);
          return of([]);
        })
      )
      .subscribe((indices) => {
        if (!indices || indices.length === 0) return;

        const requests: Observable<{
          indexName: string;
          type: 'DOCUMENT_INGESTION' | 'WEB_CRAWL';
          jobs: any[];
        }>[] = [];

        for (const idx of indices) {
          if (!idx.name) continue;
          requests.push(
            this.getQueuedTasks(idx.name, 'DOCUMENT_INGESTION').pipe(
              map((jobs) => ({
                indexName: idx.name,
                type: 'DOCUMENT_INGESTION' as const,
                jobs: jobs || [],
              })),
              catchError(() =>
                of({ indexName: idx.name, type: 'DOCUMENT_INGESTION' as const, jobs: [] })
              )
            ),
            this.getQueuedTasks(idx.name, 'WEB_CRAWL').pipe(
              map((jobs) => ({
                indexName: idx.name,
                type: 'WEB_CRAWL' as const,
                jobs: jobs || [],
              })),
              catchError(() =>
                of({ indexName: idx.name, type: 'WEB_CRAWL' as const, jobs: [] })
              )
            )
          );
        }

        if (requests.length === 0) return;

        forkJoin(requests).subscribe((results) => {
          const fetchedTasks = this.mapResultsToTasks(results);
          this.tasks.update((currentList) => this.mergeFetchedTasks(currentList, fetchedTasks));
        });
      });
  }

  private mapResultsToTasks(
    results: { indexName: string; type: 'DOCUMENT_INGESTION' | 'WEB_CRAWL'; jobs: any[] }[]
  ): TaskNotificationItem[] {
    const fetchedTasks: TaskNotificationItem[] = [];
    for (const res of results) {
      for (const job of res.jobs || []) {
        fetchedTasks.push(this.parseJobToTaskNotification(job, res));
      }
    }
    return fetchedTasks;
  }

  private parseJobProgress(progress: any, isStarted: boolean): { progress: number; step: string; progressMessage: string } {
    let progressVal = 0;
    let step = isStarted ? 'Running' : 'Waiting';
    let progressMessage = '';

    if (progress !== undefined) {
      if (typeof progress === 'object' && progress !== null) {
        progressVal = progress.percent ?? progress.progress ?? 0;
        step = progress.step ?? step;
        progressMessage = progress.message ?? '';
      } else if (typeof progress === 'number') {
        progressVal = progress;
      }
    }
    return { progress: progressVal, step, progressMessage };
  }

  private parseJobToTaskNotification(job: any, res: { indexName: string; type: 'DOCUMENT_INGESTION' | 'WEB_CRAWL' }): TaskNotificationItem {
    const taskId = job.data?.taskId || job.id;
    const metadata = job.data?.metadata || {};
    const label = this.extractLabel(metadata, res.type);
    const isStarted = (job.attemptsStarted && job.attemptsStarted > 0) || !!job.processedOn;
    const { progress, step, progressMessage } = this.parseJobProgress(job.progress, isStarted);

    return {
      id: taskId || String(job.id),
      jobId: String(job.id),
      taskId,
      indexName: metadata.indexName || res.indexName,
      label,
      type: res.type === 'DOCUMENT_INGESTION' ? 'DOCUMENT' : 'WEB',
      status: isStarted ? 'active' : 'waiting',
      step,
      progress,
      progressMessage,
      updatedAt: new Date(),
    };
  }

  private mergeFetchedTasks(currentList: TaskNotificationItem[], fetchedTasks: TaskNotificationItem[]): TaskNotificationItem[] {
    const map = new Map<string, TaskNotificationItem>();
    for (const t of currentList) {
      map.set(t.id, t);
    }
    for (const ft of fetchedTasks) {
      const existing = map.get(ft.id);
      if (!existing) {
        map.set(ft.id, ft);
      } else if (existing.status !== 'completed' && existing.status !== 'cancelling' && existing.status !== 'cancelled') {
        map.set(ft.id, {
          ...existing,
          ...ft,
          progress: Math.max(existing.progress, ft.progress),
        });
      }
    }
    return Array.from(map.values());
  }

  private taskListeners() {
    this.socket.on('task:waiting', (data: any) => {
      this.handleTaskWaiting(data);
      this.taskEvents.next({ type: 'waiting', ...data });
    });

    this.socket.on('task:active', (data: any) => {
      this.handleTaskActive(data);
      this.taskEvents.next({ type: 'active', ...data });
    });

    this.socket.on('task:progress', (data: any) => {
      this.handleTaskProgress(data);
      this.taskEvents.next({ type: 'progress', ...data });
    });

    this.socket.on('task:completed', (data: any) => {
      this.handleTaskCompleted(data);
      this.taskEvents.next({ type: 'completed', ...data });
    });

    this.socket.on('task:error', (data: any) => {
      this.handleTaskError(data);
      this.taskEvents.next({ type: 'error', ...data });
    });

    this.socket.on('task:cancelled', (data: any) => {
      this.handleTaskCancelled(data);
      this.taskEvents.next({ type: 'cancelled', ...data });
    });
  }

  /** Queued tasks are removed right away; running ones stop at the worker's next checkpoint. */
  public cancelTask(task: TaskNotificationItem): Observable<{ status: 'CANCELLED' | 'CANCELLING' }> {
    const previousStatus = task.status;
    this.setTaskStatus(task.id, 'cancelling');

    return this.http.delete<{ status: 'CANCELLED' | 'CANCELLING' }>(`${environment.apiUrl}/task/${task.taskId || task.jobId}`).pipe(
      tap((res) => {
        if (res.status === 'CANCELLED') this.setTaskStatus(task.id, 'cancelled');
      }),
      catchError((err) => {
        this.setTaskStatus(task.id, previousStatus);
        throw err;
      })
    );
  }

  private setTaskStatus(id: string, status: TaskNotificationItem['status']) {
    this.tasks.update((list) =>
      list.map((t) =>
        t.id === id
          ? {
              ...t,
              status,
              progressMessage: status === 'cancelling' ? 'Annullamento in corso...' : t.progressMessage,
              updatedAt: new Date(),
            }
          : t
      )
    );
  }

  private handleTaskCancelled(data: any) {
    const key = data.taskId || String(data.jobId);
    this.tasks.update((list) =>
      list.map((t) =>
        t.id === key || t.taskId === data.taskId || t.jobId === String(data.jobId)
          ? { ...t, status: 'cancelled', step: 'CANCELLED', progressMessage: 'Annullato dall\'utente', updatedAt: new Date() }
          : t
      )
    );
  }

  private handleTaskWaiting(data: any) {
    const key = data.taskId || String(data.jobId);
    const metadata = data.metadata || {};
    const type: TaskNotificationItem['type'] = data.type === 'WEB' ? 'WEB' : 'DOCUMENT';

    this.tasks.update((list) => {
      const idx = list.findIndex(
        (t) => t.id === key || t.taskId === data.taskId || t.jobId === String(data.jobId)
      );

      if (idx === -1) {
        const newTask: TaskNotificationItem = {
          id: key,
          jobId: data.jobId ? String(data.jobId) : undefined,
          taskId: data.taskId,
          indexName: metadata.indexName,
          label: this.extractLabel(metadata, type),
          type,
          status: 'waiting',
          step: 'In coda',
          progress: 0,
          updatedAt: new Date(),
        };
        return [newTask, ...list];
      }

      // A known task goes back to waiting only when it is being retried after a failure
      const updated = [...list];
      updated[idx] = {
        ...updated[idx],
        status: this.keepCancelState(updated[idx].status, 'waiting'),
        step: 'In coda',
        progress: 0,
        progressMessage: updated[idx].status === 'waiting' ? updated[idx].progressMessage : 'In coda per un nuovo tentativo...',
        updatedAt: new Date(),
      };
      return updated;
    });
  }

  private handleTaskActive(data: any) {
    const key = data.taskId || (data.jobId ? String(data.jobId) : `task-${Date.now()}`);
    this.tasks.update((list) => {
      const idx = list.findIndex(
        (t) => t.id === key || t.taskId === data.taskId || t.jobId === String(data.jobId)
      );
      const metadata = data.metadata || {};
      const label = this.extractLabel(metadata, 'DOCUMENT');

      if (idx === -1) {
        const newTask: TaskNotificationItem = {
          id: key,
          jobId: data.jobId ? String(data.jobId) : undefined,
          taskId: data.taskId,
          indexName: metadata.indexName,
          label: label || 'Elaborazione avviata',
          type: 'DOCUMENT',
          status: 'active',
          step: 'Avvio',
          progress: 5,
          updatedAt: new Date(),
        };
        return [newTask, ...list];
      }

      const updated = [...list];
      updated[idx] = {
        ...updated[idx],
        status: this.keepCancelState(updated[idx].status, 'active'),
        step: updated[idx].step || 'In elaborazione',
        updatedAt: new Date(),
      };
      return updated;
    });
  }

  private handleTaskProgress(data: any) {
    const key = data.taskId || (data.jobId ? String(data.jobId) : `task-${Date.now()}`);
    const progressVal = data.percent ?? data.progress ?? 0;
    const stepVal = data.step;
    const messageVal = data.message;
    const metadata = data.metadata || {};

    this.tasks.update((list) => {
      const idx = list.findIndex(
        (t) => t.id === key || t.taskId === data.taskId || t.jobId === String(data.jobId)
      );
      if (idx === -1) {
        const newTask: TaskNotificationItem = {
          id: key,
          jobId: data.jobId ? String(data.jobId) : undefined,
          taskId: data.taskId,
          indexName: metadata.indexName,
          label: this.extractLabel(metadata, 'DOCUMENT'),
          type: 'DOCUMENT',
          status: 'active',
          step: stepVal || 'In corso',
          progress: progressVal,
          progressMessage: messageVal,
          updatedAt: new Date(),
        };
        return [newTask, ...list];
      }

      const updated = [...list];
      updated[idx] = {
        ...updated[idx],
        status: this.keepCancelState(updated[idx].status, 'active'),
        progress: progressVal,
        step: stepVal || updated[idx].step,
        progressMessage: messageVal || updated[idx].progressMessage,
        updatedAt: new Date(),
      };
      return updated;
    });
  }

  private handleTaskCompleted(data: any) {
    const key = data.taskId || (data.jobId ? String(data.jobId) : `task-${Date.now()}`);
    this.tasks.update((list) => {
      const idx = list.findIndex(
        (t) => t.id === key || t.taskId === data.taskId || t.jobId === String(data.jobId)
      );
      if (idx === -1) {
        const metadata = data.metadata || {};
        return [
          {
            id: key,
            jobId: data.jobId ? String(data.jobId) : undefined,
            taskId: data.taskId,
            indexName: metadata.indexName,
            label: this.extractLabel(metadata, 'DOCUMENT'),
            type: 'DOCUMENT',
            status: 'completed',
            progress: 100,
            step: 'COMPLETED',
            progressMessage: 'Completato con successo',
            updatedAt: new Date(),
          },
          ...list,
        ];
      }

      const updated = [...list];
      updated[idx] = {
        ...updated[idx],
        status: 'completed',
        progress: 100,
        step: 'COMPLETED',
        progressMessage: 'Completato con successo',
        updatedAt: new Date(),
      };
      return updated;
    });
  }

  private handleTaskError(data: any) {
    const key = data.taskId || (data.jobId ? String(data.jobId) : `task-${Date.now()}`);
    this.tasks.update((list) => {
      const idx = list.findIndex(
        (t) => t.id === key || t.taskId === data.taskId || t.jobId === String(data.jobId)
      );
      if (idx === -1) {
        return list;
      }

      const updated = [...list];
      updated[idx] = {
        ...updated[idx],
        status: 'error',
        step: 'FAILED',
        error: data.error || 'Errore durante l\'elaborazione',
        updatedAt: new Date(),
      };
      return updated;
    });
  }

  private extractLabel(metadata: any, type: string): string {
    if (!metadata) return 'Documento';
    if (metadata.filePath) {
      const parts = metadata.filePath.split('/');
      return parts.pop() || metadata.filePath;
    }
    if (metadata.fileName) return metadata.fileName;
    if (metadata.url) return metadata.url;
    if (metadata.urls) {
      return Array.isArray(metadata.urls) ? metadata.urls.join(', ') : String(metadata.urls);
    }
    return type === 'DOCUMENT' || type === 'DOCUMENT_INGESTION' ? 'Documento' : 'Sito Web';
  }

  public clearCompletedTasks() {
    this.tasks.update((list) => list.filter((t) => TaskService.isPending(t.status)));
  }

  static isPending(status: TaskNotificationItem['status']): boolean {
    return status === 'active' || status === 'waiting' || status === 'cancelling';
  }

  // Progress events can still arrive after a cancel request: they must not bring the task back to active
  private keepCancelState(current: TaskNotificationItem['status'], next: TaskNotificationItem['status']): TaskNotificationItem['status'] {
    return current === 'cancelling' || current === 'cancelled' ? current : next;
  }

  public removeTask(id: string) {
    this.tasks.update((list) => list.filter((t) => t.id !== id));
  }

  getTaskEvents(): Observable<any> {
    return this.taskEvents.asObservable();
  }

  disconnect() {
    this.socket.disconnect();
  }
}